import { useState } from 'react';

import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';

import { Button } from 'components/Button';
import { Checkbox } from 'components/Checkbox';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { formatCurrency } from 'utils/formatCurrency';
import { cn } from 'utils/shadcn';

import { LTLQuote } from '../types';
import { getLTLQuoteIdentity, getLTLSellRate } from '../util';
import { LTLRateCard } from './LTLRateCard';
import { LTLRateCardCoverageConfig } from './LTLRateCardCoverage';

const COLLAPSED_ROW_COUNT = 5;
const RATE_FRACTION_DIGITS = 2;

type LTLCompactRateListProps = {
  // Carriers with rates, cheapest first.
  rateableQuotes: LTLQuote[];
  // Carriers that errored or do not service the lane; listed greyed out, never selectable.
  unavailableQuotes: LTLQuote[];
  selectedQuoteIds: string[];
  onSelectQuote: (quote: LTLQuote) => void;
  coverageConfig?: LTLRateCardCoverageConfig;
  hideCoverage: boolean;
};

// One dense row per carrier (service level, guaranteed, transit, sell rate); a row opens
// into the full LTL rate card with the buy vs sell breakdown and coverage.
export function LTLCompactRateList({
  rateableQuotes,
  unavailableQuotes,
  selectedQuoteIds,
  onSelectQuote,
  coverageConfig,
  hideCoverage,
}: LTLCompactRateListProps) {
  const [isShowingAll, setIsShowingAll] = useState(false);
  const [expandedQuoteIds, setExpandedQuoteIds] = useState<string[]>([]);
  const rows = [...rateableQuotes, ...unavailableQuotes];
  const visibleRows = isShowingAll ? rows : rows.slice(0, COLLAPSED_ROW_COUNT);
  const hiddenCount = rows.length - visibleRows.length;

  const toggleExpanded = (quoteId: string) =>
    setExpandedQuoteIds((prev) =>
      prev.includes(quoteId)
        ? prev.filter((id) => id !== quoteId)
        : [...prev, quoteId]
    );

  return (
    <Flex
      direction='col'
      gap='xs'
      className='w-full'
      data-testid='ltl-compact-rate-list'
    >
      <Flex
        align='center'
        gap='sm'
        className='w-full border-b border-neutral-200 pb-1 pl-6 pr-5 text-neutral-500'
      >
        <Typography variant='body-xxs' weight='semibold' className='flex-1'>
          Carrier
        </Typography>
        <Typography
          variant='body-xxs'
          weight='semibold'
          align='right'
          className='w-10'
        >
          Transit
        </Typography>
        <Typography
          variant='body-xxs'
          weight='semibold'
          align='right'
          className='w-[4.5rem]'
        >
          Rate
        </Typography>
      </Flex>

      {visibleRows.map((quote) => {
        const quoteId = getLTLQuoteIdentity(quote);
        const isUnavailable = unavailableQuotes.includes(quote);
        const isSelected = selectedQuoteIds.includes(quoteId);
        const isExpanded = expandedQuoteIds.includes(quoteId);

        if (isUnavailable) {
          return (
            <Flex
              key={quoteId}
              align='start'
              gap='sm'
              className='w-full px-0.5 py-0.5 pl-6 opacity-60'
              data-testid='ltl-compact-rate-unavailable'
            >
              <Flex direction='col' className='min-w-0 flex-1'>
                <Typography
                  variant='body-xs'
                  textColor='muted'
                  className='truncate'
                  title={quote.carrierName}
                >
                  {quote.carrierName}
                </Typography>
                <Typography variant='body-xxs' textColor='muted'>
                  {getUnavailableReason(quote)}
                </Typography>
              </Flex>
              <Typography
                variant='body-xs'
                textColor='muted'
                align='right'
                className='w-[4.5rem] shrink-0 pr-5'
              >
                No rate
              </Typography>
            </Flex>
          );
        }

        return (
          <Flex key={quoteId} direction='col' className='w-full'>
            <Flex
              align='center'
              gap='sm'
              className={cn(
                'w-full rounded px-0.5 py-0.5',
                isSelected && 'bg-info-50'
              )}
            >
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => onSelectQuote(quote)}
                aria-label={`Include ${quote.carrierName}`}
              />
              <Flex direction='col' className='min-w-0 flex-1'>
                <Typography
                  variant='body-xs'
                  className='truncate leading-tight'
                  title={quote.carrierName}
                >
                  {quote.carrierName}
                </Typography>
                {(quote.serviceLevel || quote.guaranteed) && (
                  <Typography variant='body-xxs' textColor='muted'>
                    {[quote.serviceLevel, quote.guaranteed && 'Guaranteed']
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                )}
              </Flex>
              <Typography
                variant='body-xs'
                textColor='muted'
                align='right'
                className='w-10 shrink-0 tabular-nums'
              >
                {quote.transitDays > 0 ? `${quote.transitDays}d` : 'N/A'}
              </Typography>
              <Typography
                variant='body-xs'
                weight='semibold'
                align='right'
                className='w-[4.5rem] shrink-0 tabular-nums'
              >
                {formatCurrency(
                  getLTLSellRate(quote),
                  'USD',
                  RATE_FRACTION_DIGITS
                )}
              </Typography>
              <button
                type='button'
                className='shrink-0 text-neutral-500 hover:text-neutral-700'
                aria-expanded={isExpanded}
                aria-label={`${isExpanded ? 'Hide' : 'Show'} rate details for ${quote.carrierName}`}
                onClick={() => toggleExpanded(quoteId)}
              >
                {isExpanded ? (
                  <ChevronUpIcon className='h-3.5 w-3.5' />
                ) : (
                  <ChevronDownIcon className='h-3.5 w-3.5' />
                )}
              </button>
            </Flex>
            {isExpanded && (
              <LTLRateCard
                quote={quote}
                coverageConfig={coverageConfig}
                hideCoverage={hideCoverage}
                isSelected={isSelected}
                onClick={() => onSelectQuote(quote)}
              />
            )}
          </Flex>
        );
      })}

      {(hiddenCount > 0 || isShowingAll) && (
        <Button
          type='button'
          variant='ghost'
          size='sm'
          className='h-6 self-end px-1 text-xs text-info-600 hover:bg-info-50'
          onClick={() => setIsShowingAll((prev) => !prev)}
          buttonNamePosthog={null}
        >
          {isShowingAll ? 'Show fewer' : `Show ${hiddenCount} more`}
        </Button>
      )}
    </Flex>
  );
}

function getUnavailableReason(quote: LTLQuote): string {
  if (quote.notServiced) {
    return quote.errorMessage || 'Cannot service this lane';
  }

  return quote.errorMessage || 'Unable to quote';
}
